import * as THREE from 'three';
import { G, Pool, mesh, scene, stdMat } from '../render/scene';
import { registerPool } from '../render/fx-instances';
import { texPolyRing, texRing, texSpark } from '../render/vfx-tex';
import { texNeedle, texPolyBand, texPolyFill } from '../render/vfx-tex2';
/* ---------- 投射物與特效池 ---------- */
/* 彈體：冷白（第四版硬邊幾何／白光）。 */
export var MAT_BULLET = new THREE.MeshBasicMaterial({ color: 0xf2f8ff });
export var MAT_EBULLET = new THREE.MeshBasicMaterial({ color: 0xff6a80 });
/* 飛彈**彈體不發光**（2026-08-05：使用者回報飛彈魔法感太重）。
   原本 `em: 0xffb454, ei: 1.4` 讓整枚彈身在暗場景裡自體發亮 ——
   一根會發光的橘色棒子飛過去，那就是魔法飛彈。
   實彈的飛彈是**暗的**：只有尾焰那一小段亮，而亮的是尾焰不是彈殼。 */
/* 彈體顏色第三次上調（使用者 2026-08-05：飛彈再顯眼一些）。
   0x3a3f47 → 0x5a6068 → 0x7d8794。**這是提高固有色不是加自體發光** ——
   加 emissive 會讓它變回會發亮的魔法飛彈，而提高固有色只是把它
   從「深色的東西」變成「淺色的東西」，在深色地面上一樣顯眼、但仍然吃光影。 */
export var MAT_MISSILE = stdMat(0x7d8794, { metal: 0.5, rough: 0.42, em: 0x201408, ei: 0.7, flat: true });
/** 尾焰：只掛在彈尾那一小塊。加法混色但面積極小。 */
export var MAT_MISSILE_JET = new THREE.MeshBasicMaterial({
  color: 0xffab52, transparent: true, opacity: 0.7,
  blending: THREE.AdditiveBlending, depthWrite: false
});
/* 地雷第四版（硬邊幾何／白光）：拿掉 emissive 的暖橙，改成暗鋼件。
   亮的部分交給下面的 `MAT_MINE_LIT`（一般混色的白，不是加法的光）。 */
export var MAT_MINE = stdMat(0x232c3a, { metal: 0.45, rough: 0.6, flat: true });
/** 地雷的發光鑲邊。加法混色、不透明度壓在泛光門檻下 —— 它要看得見，
    但不該在滿地六顆的時候把畫面點亮（使用者 2026-08-05：不要太炫光）。 */
export var MAT_MINE_LIT = new THREE.MeshBasicMaterial({
  color: 0xf2f8ff, transparent: true, opacity: 0.92, depthWrite: false
});

export var bullets = new Pool(260, function () {
  /* who：傷害歸因用的細粒度標籤，跟 damageEnemy 的 src（衝擊效率查表鍵）分開，
     見 sim/fx.ts 的 damageEnemy 檔頭註解與 .claude/notes/damage-paths.md。 */
  return { m: mesh(G.box, MAT_BULLET, [0.16, 0.16, 1.5]), v: new THREE.Vector3(), life: 0, dmg: 0, crit: false, pierce: 0, chain: 0, hitList: [], who: '' };
});
export var ebullets = new Pool(220, function () {
  return { m: mesh(G.oct, MAT_EBULLET, [0.5, 0.5, 0.5]), v: new THREE.Vector3(), life: 0, dmg: 0 };
});
export var missiles = new Pool(40, function () {
  var g = new THREE.Group();
  /* 尺寸放大一輪（使用者 2026-08-05：飛彈做得顯眼一點）。
     彈體 0.28 → 0.44 寬、彈長 1.1 → 1.5，尾翼加大 ——
     俯視鏡頭下 0.28 寬的東西只有兩三個像素，飛出去就消失了。
     **亮度沒有跟著加**：顯眼靠體積與尾焰，不靠自體發光（那會變回魔法飛彈）。 */
  g.add(mesh(G.box, MAT_MISSILE, [0.56, 0.56, 1.85]));
  g.add(mesh(G.cone, MAT_MISSILE, [0.66, 0.85, 0.66], [0, 0, 1.25], [Math.PI / 2, 0, 0]));
  g.add(mesh(G.box, MAT_MISSILE, [1.7, 0.16, 0.6], [0, 0, -0.75]));
  g.add(mesh(G.box, MAT_MISSILE, [0.16, 1.4, 0.6], [0, 0, -0.75]));
  g.add(mesh(G.cone, MAT_MISSILE_JET, [0.52, 1.5, 0.52], [0, 0, -1.6], [-Math.PI / 2, 0, 0]));
  /* `trail`：`render/trails.ts` 的緞帶編號，-1 = 還沒借。飛彈是唯一會爬升、
     轉彎的投射物，只有真的緞帶跟得上它的軌跡（曳光面片只能表達直線）。 */
  /* `arc`：1 = 無動力彈道（TITAN-3 的榴彈／分裂彈，只吃重力、不吐排氣煙、
     尾焰噴口關掉）。0 = 有推力的飛彈。見 `sim/projectiles.ts` 的說明。 */
  return { m: g, v: new THREE.Vector3(), life: 0, target: null, dmg: 0, radius: 3.4, split: 0, scale: 1, arc: 0, who: '', trail: -1 };
});
/* 六角柱：這一版所有玩家裝備的共用底（同 `render/gadgets.ts`）。
   10 段的 `G.cyl` 讀成圓柱，6 段才讀成機械件。 */
var HEX6 = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
export var mines = new Pool(18, function () {
  var g = new THREE.Group();
  /* 扁平六角基座 + 浮起來的小核心（極簡霓虹幾何，2026-08-05）。
     舊版是「圓柱 + 八面體」，在俯視鏡頭下是一顆分不出是什麼的暗色球，
     而地雷最重要的資訊是**它佔了地上這一塊**。 */
  g.add(mesh(HEX6, MAT_MINE, [1.35, 0.16, 1.35], [0, 0.08, 0]));
  g.add(mesh(HEX6, MAT_MINE_LIT, [1.55, 0.06, 1.55], [0, 0.08, 0]));
  g.add(mesh(HEX6, MAT_MINE_LIT, [0.34, 0.34, 0.34], [0, 0.5, 0]));
  /* 感應範圍框（`children[3]`，由 `sim/projectiles.ts` 每幀縮放到 `trigR`）。
     **這是 Lv3 質變的全部意義**：接觸雷 → 感應雷，觸發範圍 ×2.2。
     範圍看不見的話，玩家升到 Lv3 只會覺得「好像比較容易爆」。 */
  var trig = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.MeshBasicMaterial({
    map: texPolyBand(6, 0.045), color: 0xdce8f2, transparent: true, opacity: 0.16,
    depthWrite: false, side: THREE.DoubleSide
  }));
  trig.rotation.x = -Math.PI / 2; trig.position.y = 0.06;
  g.add(trig);
  /* trigR / blast / ttl：感應範圍、爆炸半徑、存在時間。每顆自己帶，
     讓「範圍」與「持續」兩條統計軸有東西可以動（見 sim/projectiles.ts）。
     who：傷害歸因標籤，w-mine／e-minefield／機體小招「地雷」改裝三者共用這個池子，
     各自在 .get() 之後填自己的來源 id。 */
  return { m: g, life: 0, arm: 0, dmg: 0, trigR: 0, blast: 0, ttl: 0, who: '' };
});
export var strikes = new Pool(10, function () {
  var g = new THREE.Group();
  /* 落點的六角框（極簡霓虹幾何，2026-08-05）。用程序貼圖的線稿而不是
     `G.ring` —— 那是不發光的實心環，線寬也改不了。
     PlaneGeometry 開 2×2 是為了讓 `scale.setScalar(radius)` 的語意不變
     （既有呼叫端都是這樣寫的）。 */
  var ring = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.MeshBasicMaterial({
    map: texPolyBand(6, 0.05), color: 0xdce8f2, transparent: true, opacity: 0.5,
    depthWrite: false, side: THREE.DoubleSide
  }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.1; g.add(ring);
  // 光柱改六角截面：圓柱在俯視下是一個沒有個性的亮圓
  /* 光柱是**六角柱**（使用者 2026-08-05 指定）。`flatShading` 讓六個面
     各自一個亮度 —— 少了它，加法混色的六角柱在畫面上跟圓柱沒兩樣。
     另外套一層比它大一點的線框，柱子的六個角才咬得住。 */
  /* 柱體維持加法混色（它就是一道光），但改成白 —— 冷藍是第三版之前
     留下來的色，而這一版全遊戲只有白熱一階。泛光門檻抬到 0.84 之後
     它不會再暈成一根光棒。 */
  var col = mesh(HEX6, new THREE.MeshBasicMaterial({ color: 0xdce8f2, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }), [1, 60, 1], [0, 30, 0]);
  g.add(col);
  /* who：傷害歸因標籤，w-orbital／e-dominion／u-lattice／機體大招「軌道支援」改裝
     四者共用這個池子，各自在 .get() 之後填自己的來源 id。 */
  return { m: g, ring: ring, col: col, life: 0, delay: 0.75, radius: 6, dmg: 0, fired: false, who: '' };
});
export var shocks = new Pool(14, function () {
  var m = new THREE.Mesh(G.ring, new THREE.MeshBasicMaterial({ color: 0xff6a80, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  m.rotation.x = -Math.PI / 2; m.position.y = 0.14;
  return { m: m, r: 1, rmax: 30, spd: 18, dmg: 0, hit: false };
});
export var debris = new Pool(340, function () {
  return { m: new THREE.Mesh(G.box, new THREE.MeshStandardMaterial({ color: 0xffffff, flatShading: true, metalness: 0.4, roughness: 0.6 })), v: new THREE.Vector3(), av: new THREE.Vector3(), life: 0, max: 1, s0: 1 };
});
export var rings = new Pool(30, function () {
  var m = new THREE.Mesh(G.ring, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  m.rotation.x = -Math.PI / 2;
  return { m: m, life: 0, max: 0.5, s0: 1, s1: 6 };
});
export var MAT_ORB = stdMat(0x0a1a12, { em: 0x7dffa8, ei: 2.0, flat: true });
export var MAT_HEAL = stdMat(0x1a0a12, { em: 0xff6fa8, ei: 2.0, flat: true });
export var orbs = new Pool(320, function () {
  return { m: mesh(G.oct, MAT_ORB, [0.42, 0.42, 0.42]), v: new THREE.Vector3(), life: 0, xp: 1, heal: 0, pulling: false };
});

/* 寶箱：精英擊破後掉落。**不會被磁力吸過來** —— 玩家得自己走過去撿，
   而那趟路正是它的設計重點（VS 的寶箱也是這樣）：畫面最亂的時候，
   要不要為了它穿過敵群，是一個真的決策。 */
export var MAT_CHEST = new THREE.MeshStandardMaterial({
  color: 0x1a2b3a, emissive: 0xffd24a, emissiveIntensity: 1.4, flatShading: true, metalness: 0.4, roughness: 0.4
});
/* 這四個池子接上實例化渲染（M8-d）：碎片 340 + 經驗球 320 + 子彈 260 + 敵彈 220
   = 最多 1140 個 draw call，而且戰鬥愈激烈愈多。
   註冊在這裡而不是各自的模組，是因為要在池子建好之後、任何一幀之前做完。
   經驗球與補血球材質不同（綠 / 粉），所以分兩次註冊、各挑一半。 */
export function registerFxPools() {
  registerPool(debris, G.box, new THREE.MeshStandardMaterial({
    color: 0xffffff, flatShading: true, metalness: 0.4, roughness: 0.6
  }), { tint: true });                       // 碎片顏色隨敵人，走 instanceColor
  /* 曳光：**同一個子彈池註冊第二次**，只是換一張躺平的長條貼圖再放大。
     一顆會位移的白方塊讀不出速度 —— 拖在後面的那條光才是速度感的來源，
     而且子彈飛過去之後，「剛剛那一發從哪打到哪」還留在畫面上 0.1 秒。
     成本是一個 draw call（實例化），不是每發一個。
     幾何體先轉兩次是為了讓長軸對準子彈的前進方向：
     rotateX 把面片放平，rotateY 把長軸從世界 X 轉到世界 Z。
     **曳光是暖橙的尖尾**（實彈，2026-08-05 第三次定調）：青色的方頭線段
     讀成能量武器，橙色的尖尾才讀成曳光彈。子彈本體是白熱的彈頭。 */
  /* 哪些武裝有專屬彈體（下面 `variant()` 會各註冊一次）。
     宣告放在這裡是因為曳光那一層的 `pick` 也要用它排除變體。 */
  var VARIANT: Record<string, 1> = { 'e-piercer': 1, 'e-bladewall': 1, 'e-endless': 1, 'u-storm': 1 };

  /* 曳光第四版：**硬邊白針**取代暖橙的柔邊尖尾。
     `texSpark` 是柔邊漸層 + 加法混色 —— 在泛光下就是一條會發光的線，
     而使用者對「跟著移動物體畫的加法線」的評語是「很像魔法」。
     `texNeedle` 兩端收尖但邊界銳利，配一般混色：一樣讀得出速度與方向，
     但它是「一片被切出來的東西」而不是「一道光」。 */
  var streak = texNeedle();
  if (streak) {
    var tracerGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).rotateY(Math.PI / 2);
    registerPool(bullets, tracerGeo, new THREE.MeshBasicMaterial({
      map: streak, color: 0xdce8f2, transparent: true, opacity: 0.85,
      depthWrite: false, side: THREE.DoubleSide
    }), { pick: function (o) { return !VARIANT[o.who]; }, scale: [4.6, 1, 2.0] });
    /* 敵彈**不給曳光**：它們每幀繞兩軸自轉（`sim/projectiles.ts`），
       躺平的長條會跟著翻成一團看不懂的東西。敵彈的辨識度靠形狀與顏色，
       那條規則（敵人投射物必須高於玩家特效）另外處理。 */
  }
  /* ---------- 質變武裝的專屬彈體 ----------

     十八條質變裡有七條是「定時發射」型，而它們全部共用同一顆白方塊 ——
     玩家進化之後畫面上完全看不出差別（`.claude/notes/` 的質變盤點）。

     解法是**同一個子彈池註冊多次、用 `pick` 依 `who` 分流**
     （跟經驗球／補血球同一招）。一把武裝一個 draw call，
     而且呼叫端一行都不用改。

     形狀對著「這把武裝拿掉了什麼限制」給：
       穿甲 → 細長的針（它會穿過去）
       刃幕 → 橫向的寬刃（它是一面牆）
       反射 → 六角盤（它會彈、會分裂，圓盤才讀得出翻滾） */
  function variant(who: string, tex: any, alongZ: boolean, sc: number[]) {
    if (!tex) return;
    var geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    if (alongZ) geo.rotateY(Math.PI / 2);      // 長軸從局部 X 轉到局部 Z（＝飛行方向）
    registerPool(bullets, geo, new THREE.MeshBasicMaterial({
      map: tex, color: 0xf2f8ff, transparent: true, opacity: 0.95,
      depthWrite: false, side: THREE.DoubleSide
    }), { pick: function (o) { return o.who === who; }, scale: sc });
  }
  var nd = texNeedle(), hx = texPolyFill(6);
  variant('e-piercer', nd, true, [3.0, 1, 2.2]);      // 細長針，沿飛行方向
  variant('e-bladewall', nd, false, [1.05, 1, 3.0]);  // 寬刃，橫過飛行方向
  variant('e-endless', hx, false, [6.0, 1, 0.9]);     // 小六角盤
  variant('u-storm', hx, false, [8.0, 1, 1.2]);       // 大一號的六角盤

  /* 通用彈體與曳光都要**排除**變體，否則同一顆子彈會被畫兩次
     （一次白方塊、一次專屬造型），變體看起來就像糊了一層。 */
  registerPool(bullets, G.box, MAT_BULLET, { pick: function (o) { return !VARIANT[o.who]; } });
  registerPool(ebullets, G.oct, MAT_EBULLET);
  registerPool(orbs, G.oct, MAT_ORB, { pick: function (o) { return !o.heal; } });
  registerPool(orbs, G.oct, MAT_HEAL, { pick: function (o) { return !!o.heal; } });
}

export var chests = new Pool(8, function () {
  var g = new THREE.Group();
  /* 尺寸是實看截圖之後放大的。第一版 1.5 單位寬，在俯視鏡頭下是一個
     幾乎找不到的小黃點 —— 而「要走過去撿」的前提是你看得到它在哪。 */
  g.add(mesh(G.box, MAT_CHEST, [2.6, 1.5, 1.9], [0, 0.9, 0]));
  g.add(mesh(G.box, MAT_CHEST, [3.0, 0.35, 2.3], [0, 1.75, 0]));
  var ring = new THREE.Mesh(G.ring, new THREE.MeshBasicMaterial({
    color: 0xffd24a, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false
  }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.08; ring.scale.setScalar(3.4);
  g.add(ring);
  // 光柱：從畫面外也看得到「那邊有一個寶箱」
  var beam = new THREE.Mesh(G.cyl, new THREE.MeshBasicMaterial({
    color: 0xffd24a, transparent: true, opacity: 0.15, depthWrite: false
  }));
  beam.scale.set(2.4, 20, 2.4); beam.position.y = 10;
  g.add(beam);
  /* `mw` = 這一箱是**機體武裝強化**（精英首領的小機率掉落）而不是三次升級。
     ring／beam 的材質是**每個實例自己一份**（就在上面 new 出來的），
     所以顏色可以逐箱改 —— 撿之前就看得出來這是哪一種箱子，
     那是「要不要為它穿過敵群」這個決定的前提。 */
  /* `tier` = 銅／銀／金（`sim/chest.ts` 的 TIERS）。決定開出幾件的機率、
     能不能出進化，以及環與光柱的顏色 —— **顏色要在撿之前就看得到**，
     那是「要不要為它穿過敵群」這個決定的前提。 */
  return { m: g, life: 0, tier: 'silver', mw: false, ring: ring, beam: beam };
});

/* 鎖定準星：套在鎖定目標身上的青色環。手動鎖定若沒有視覺回饋，
   玩家不會知道自己鎖到了誰，集中火力這件事就無從談起。 */
export var lockRing = (function () {
  var m = new THREE.Mesh(G.ring, new THREE.MeshBasicMaterial({
    color: 0x5fe3ff, transparent: true, opacity: 0.9,
    blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide
  }));
  m.rotation.x = -Math.PI / 2; m.visible = false; scene.add(m);
  return m;
})();

/* ---------- 傳送門與獎勵台 ---------- */
export var portal = (function () {
  var g = new THREE.Group();
  var mt = new THREE.MeshBasicMaterial({ color: 0x9fe8ff, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false });
  var t1 = mesh(G.torus, mt, [7, 7, 7], [0, 3.2, 0], [Math.PI / 2, 0, 0]);
  var t2 = mesh(G.torus, mt, [5.2, 5.2, 5.2], [0, 3.2, 0], [Math.PI / 2, 0, 0]);
  var disc = new THREE.Mesh(G.circle, new THREE.MeshBasicMaterial({ color: 0x2f9fd0, transparent: true, opacity: 0.42, blending: THREE.AdditiveBlending, depthWrite: false }));
  disc.rotation.x = -Math.PI / 2; disc.position.y = 0.16; disc.scale.setScalar(3.4);
  g.add(t1); g.add(t2); g.add(disc);
  g.visible = false; scene.add(g);
  return { g: g, t1: t1, t2: t2, disc: disc, x: 0, z: 0, active: false };
})();

export var pedestal = (function () {
  var g = new THREE.Group();
  var base = stdMat(0x1c2636, { metal: 0.5, rough: 0.5, flat: true });
  var glow = stdMat(0x0a1a12, { em: 0x7dffa8, ei: 2.2, flat: true });
  g.add(mesh(G.cyl, base, [3.2, 0.6, 3.2], [0, 0.3, 0]));
  g.add(mesh(G.cyl, base, [1.4, 1.6, 1.4], [0, 1.2, 0]));
  var gem = mesh(G.oct, glow, [1.5, 1.5, 1.5], [0, 3.0, 0]);
  g.add(gem);
  var ring = new THREE.Mesh(G.ring, new THREE.MeshBasicMaterial({ color: 0x7dffa8, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.12; ring.scale.setScalar(4.5);
  g.add(ring);
  g.visible = false; scene.add(g);
  return { g: g, gem: gem, ring: ring, x: 0, z: 0, active: false };
})();
